import React, { type JSX } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/** Custom styling for the markdown elements a document can contain. */
const MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-4 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => <h2 className="text-xl font-bold text-gray-900 mt-5 mb-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h3>,
  p: ({ children }) => <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-3 text-gray-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-3 text-gray-700">{children}</ol>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-blue-200 pl-4 my-3 text-gray-600 italic">
      {children}
    </blockquote>
  ),
  code: ({ children, ...props }) => {
    const isInline = !props.className || !props.className.startsWith("language-");
    return isInline ? (
      <code className="bg-gray-100 px-1 py-0.5 rounded-sm text-sm font-mono text-gray-800">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-100 p-3 rounded-sm text-sm font-mono text-gray-800 overflow-x-auto">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-gray-100 p-3 rounded-sm mb-3 overflow-x-auto">{children}</pre>
  ),
};

/** Rendered markdown content. */
export function MarkdownView({ text }: { text: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
            {text}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
